import React, { useContext } from 'react';
import proptypes from 'prop-types';
const { oneOf, bool: boolType, number: numberType, string: stringType } = proptypes;
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { Orders } from '@one-thd/sui-icons';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ProductCarousel } from '../ProductCarousel';
import { getSkippers, getAppId, getImpressionID } from '../../utils/helpers';
import { priceDropDataModel } from '../../dataModel';
import { FallbackCard } from '../../core/FallbackCard';

// Resolves the UDS user id for price-drop. An explicit customerID always wins;
// otherwise logged-in shoppers are keyed by svocID and guests by mcvisID.
const getPriceDropCustID = (customerID, customerIsLoggedIn, thdCustomer) => {
  const { customerType: isLoggedIn, mcvisID, svocID } = thdCustomer || {};
  const custIsLoggedIn = customerIsLoggedIn || isLoggedIn;

  return customerID || (custIsLoggedIn ? svocID : mcvisID) || '';
};

const PriceDrop = (
  {
    brandTitleMaxLine = 2,
    customerID = null,
    customerIsLoggedIn = null,
    hideTitle = false,
    requestKey,
    showLoading = false,
    slidesPer = 2.2,
    showFallback = false,
    hideATC = true,
    noATCFulfillment = null,
    hideSavingsText = false,
    hideBulkPrice = true,
    entryId = null,
    containerName = null,
    enableRoundedProductImages = false
  }
) => {
  const { storeId, storeZip: zipCode } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext) || {};
  const custID = getPriceDropCustID(customerID, customerIsLoggedIn, useThdCustomer());

  const apiName = 'pricedrop';

  const udsQueryOptions = {
    variables: {
      apiName,
      appId: getAppId(channel, isConsumerApp),
      dataSource: apiName,
      key: requestKey,
      storeId,
      userId: custID,
      zipCode,
      ...getSkippers({})
    },
    ssr: false
  };

  const { loading, data, error } = useDataModel('uds', udsQueryOptions);

  if (error || (!data?.uds?.products?.length && !loading)) {
    if (showFallback) {
      return (
        <FallbackCard
          header="No Lower Prices On Your Items Right Now"
          subheader="Save more items to track when the prices drop,
          or check back later to see if any of your current saved items have dropped in price"
          componentName="PriceDrop"
          icon={Orders}
        />
      );
    }

    return null;
  }

  const telemetryName = containerName || '';

  const impressionData = {
    id: getImpressionID(entryId),
    name: telemetryName,
    component: PriceDrop.displayName,
    type: 'product',
    position: '',
    category: ''
  };

  return (
    <ImpressionProvider data={impressionData}>
      <div id={apiName} data-type="container" data-component="PriceDrop">
        <meta data-prop="name" content={apiName} />
        <ProductCarousel
          data={data?.uds}
          hideBulkPrice={hideBulkPrice}
          brandTitleMaxLine={brandTitleMaxLine}
          hideTitle={hideTitle}
          hideATC={hideATC}
          hideSavingsText={hideSavingsText}
          itemClass="fixed-item"
          loading={loading}
          showLoading={showLoading}
          storeId={storeId}
          zipCode={zipCode}
          slidesPer={slidesPer}
          scheme={apiName}
          noATCFulfillment={noATCFulfillment}
          hideControls={data?.uds?.products?.length <= 2}
          enableRoundedProductImages={enableRoundedProductImages}
          telemetryKey={telemetryName}
        />
      </div>
    </ImpressionProvider>
  );
};

PriceDrop.dataModel = priceDropDataModel;

PriceDrop.displayName = 'PriceDrop';

PriceDrop.propTypes = {
  // product pod takes only these prop values for brandTitleMaxLine
  brandTitleMaxLine: oneOf([2, 3, 4, null]),
  // svocid or mcvisid
  customerID: stringType,
  customerIsLoggedIn: stringType,
  hideTitle: boolType,
  requestKey: stringType.isRequired,
  showLoading: boolType,
  slidesPer: numberType,
  showFallback: boolType,
  hideATC: boolType,
  noATCFulfillment: boolType,
  hideSavingsText: boolType,
  hideBulkPrice: boolType,
  entryId: stringType,
  containerName: stringType,
  enableRoundedProductImages: boolType
};

export { PriceDrop };